import { create } from 'zustand'

// Marked by a sign-in button just before starting an attempt, consumed once by useUserInit when
// the resulting token appears. Needed because an interactive sign-in and a session quietly
// restoring on page load produce the exact same idToken transition otherwise — there is no way
// to tell them apart from the token alone.
//
// Scoped per attempt, not a bare boolean: a user can start one attempt (a Google popup) and
// switch to another (email/password) before the first one's own failure handler runs — that
// abandoned attempt's own clearInteractiveSignIn() call must not wipe the newer, still-in-flight
// attempt's mark out from under it. 0 means "nothing pending"; markInteractiveSignIn() never
// returns it, so it's safe as the empty sentinel.
//
// A store, not a bare module variable, so useInteractiveSignInPending can react to it — that's
// what lets a pending Google popup and a password submit disable each other (see
// useSignInAttemptGuard), which is the only thing that actually prevents them from racing to
// decide which account ends up signed in: Firebase's client SDK only ever tracks one signed-in
// user per tab.
const useInteractiveSignInStore = create<{ currentAttempt: number }>(() => ({ currentAttempt: 0 }))

export const markInteractiveSignIn = (): number => {
    const attempt = useInteractiveSignInStore.getState().currentAttempt + 1
    useInteractiveSignInStore.setState({ currentAttempt: attempt })
    return attempt
}

/** Atomically claims a new attempt only if none is currently pending. Unlike checking
 * useInteractiveSignInPending() and then separately calling markInteractiveSignIn(), the check
 * and the claim here happen as one synchronous read against the store's own live state — not a
 * value a caller read on some earlier render, which two entry points (see useSignInAttemptGuard)
 * could both still be holding as "not pending" in the same synchronous turn, before either has
 * rerendered to reflect the other's claim. */
export const tryMarkInteractiveSignIn = (): number | null => {
    if (useInteractiveSignInStore.getState().currentAttempt !== 0) return null
    return markInteractiveSignIn()
}

export const consumeInteractiveSignIn = (): boolean => {
    const wasPending = useInteractiveSignInStore.getState().currentAttempt !== 0
    useInteractiveSignInStore.setState({ currentAttempt: 0 })
    return wasPending
}

/** For a definite failure (wrong password, cancelled popup, MFA resolver error) or an abandoned
 * attempt (e.g. sent back to reauth): without this, the mark would sit pending and block a new
 * attempt from starting, or mislabel a later, unrelated token as this one. `attempt` is the id
 * this attempt's own markInteractiveSignIn() call returned — clearing only takes effect if it's
 * still the current attempt, so an abandoned attempt can't clear a newer one that's since taken
 * its place. */
export const clearInteractiveSignIn = (attempt: number): void => {
    if (useInteractiveSignInStore.getState().currentAttempt === attempt) {
        useInteractiveSignInStore.setState({ currentAttempt: 0 })
    }
}

/** True whenever an interactive sign-in attempt has been marked but not yet consumed or
 * cleared — reactive, so two sign-in entry points (a Google/Microsoft popup, a password submit)
 * can disable each other while either one is in flight. See useSignInAttemptGuard, which
 * combines this with the session-resolving phase that follows. */
export const useInteractiveSignInPending = (): boolean =>
    useInteractiveSignInStore((state) => state.currentAttempt !== 0)
